import clsx from "clsx";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { CustomerCaptureOverlay } from "web/react/components/customer-capture-overlay/customer-capture-overlay";
import LoadingSpinner from "web/react/components/loading-spinner/loading-spinner";
import { ProductOverlay } from "web/react/components/product-overlay";
import { PromotionInfo } from "web/react/components/promotion-summary/promotion-info/promotion-info";
import { StorefrontModal } from "web/react/components/storefront-modal";
import { StorefrontHeaderSummary } from "web/react/components/storefront-modal/storefront-header-summary";
import { container } from "web/react/emo/utilities.css";
import { View } from "web/react/emo/view";
import { ReduxStoreState } from "web/redux/store";
import analytics from "web/script/analytics/analytics";
import { gettext } from "web/script/modules/django-i18n";
import { ProductLayoutSerializer, ProductPageAreaSerializer } from "web/types/serializers";
import { Breadcrumbs } from "./breadcrumbs/breadcrumbs";
import { Footer } from "./footer/footer";
import {
    InStockProductPageContextProvider,
    ProductPurchaseType,
} from "./in-stock-product-page.context";
import { wrapper } from "./in-stock-product-page.css";
import { ProductArea } from "./product-area/product-area";
import { ProductPriceComparison } from "./product-price-comparison/product-price-comparison";
import { RelatedArea } from "./suggested-product/suggested-product";

export interface InStockAffiliateProductPageProps {
    productArea: ProductPageAreaSerializer;
    productLayout: ProductLayoutSerializer;
    helpCentreURLs?: string[];
    isOverlay?: boolean;
    parentRef?: React.RefObject<HTMLElement>;
}

export function InStockProductPage({
    productArea,
    productLayout,
    helpCentreURLs,
    isOverlay = false,
    parentRef,
}: InStockAffiliateProductPageProps): JSX.Element {
    const overlayRef = useRef(null);

    const [isModalOpen, setModalOpen] = useState(false);

    const closeStorefrontInfoModal = useCallback(() => {
        setModalOpen(false);
    }, []);

    const openStorefrontInfoModal = useCallback(() => {
        setModalOpen(true);
    }, []);

    return (
        <InStockProductPageContextProvider
            initialData={{
                productArea,
                productLayout,
                isOverlay,
                parentRef,
                openRetailerInfoModal: openStorefrontInfoModal,
            }}
        >
            {!isOverlay && <CustomerCaptureOverlay />}
            {!isOverlay && (
                <div ref={overlayRef}>
                    <ProductOverlay parentRef={overlayRef} />
                </div>
            )}
            <View className={clsx(container, wrapper)}>
                {productArea && <ProductArea />}
                {productArea.product_purchase_type !== ProductPurchaseType.EXPRESS_CHECKOUT && (
                    <ProductPriceComparison />
                )}
                <RelatedArea productLayout={productLayout} />
                <Breadcrumbs productLayout={productLayout} />
            </View>
            {helpCentreURLs && (
                <Footer productLayout={productLayout} helpCentreURLs={helpCentreURLs} />
            )}
            <StorefrontModalProvider isOpen={isModalOpen} onClose={closeStorefrontInfoModal} />
        </InStockProductPageContextProvider>
    );
}

function StorefrontModalProvider({
    isOpen,
    onClose,
}: {
    isOpen: boolean;
    onClose: () => void;
}): React.ReactElement {
    const [isPromoModalOpen, setPromoModalOpen] = useState(false);
    const { retailer, isLoading } = useSelector(
        (state: ReduxStoreState) => state.retailerInformationReducer
    );
    const storefrontModalRef = useRef<HTMLDivElement | null>(null);

    useEffect(() => {
        if (isOpen && retailer?.title && !isLoading) {
            analytics.event("store_modal", "impression", "", true, {}, "", {}, [
                { retailer_network: retailer.title },
            ]);
        }
    }, [isOpen, retailer?.title, isLoading]);

    const openPromoModal = useCallback(() => {
        setPromoModalOpen(true);
    }, []);

    const closePromoModal = useCallback(() => {
        setPromoModalOpen(false);
    }, []);

    const handleClick = useCallback(() => {
        analytics.event("visit_full_store", "clicked", "", true, {}, "", {}, [
            { retailer_network: retailer?.title },
            { parent_area: "store_modal" },
        ]);
    }, [retailer?.title]);

    return (
        <div ref={storefrontModalRef}>
            <StorefrontModal
                title={gettext("storefront.modal.title.seller_details")}
                isOpen={isOpen}
                onClose={onClose}
                onCtaClick={handleClick}
                storeFrontURL={retailer?.retailer_path || undefined}
            >
                {isLoading && (
                    <View marginY="lg">
                        <LoadingSpinner />
                    </View>
                )}
                {!isLoading && retailer && (
                    <StorefrontHeaderSummary
                        retailerName={retailer.title}
                        description={retailer.description}
                        photoUrl={retailer.photo_url}
                        smallPhotoUrl={retailer.small_photo_url}
                        followButton={retailer.follow_button}
                        followersCountLabel={retailer.followers_count_label}
                        isCheckout={retailer.checkout_verified}
                        promotions={retailer.promotions}
                        dateAdded={retailer.date_added}
                        shipsToCountry={retailer.ships_to_country}
                        bestShippingOption={retailer.best_shipping_option}
                        onClick={openPromoModal}
                    />
                )}
            </StorefrontModal>
            {!!retailer?.promotions && (
                <StorefrontModal
                    title={gettext("promotion.button.see_all")}
                    isOpen={isPromoModalOpen}
                    onClose={closePromoModal}
                >
                    <PromotionInfo promotions={retailer.promotions} />
                </StorefrontModal>
            )}
        </div>
    );
}
